import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Circle, Copy, Loader2, Unplug } from 'lucide-react'
import type { Account } from '@limo/database'
import { useAuth } from '@/contexts/AuthContext'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import { OttoLaCard } from './OttoLaCard'
import { OttoLocationUsersCard } from './OttoLocationUsersCard'
import { OttoResetCard } from './OttoResetCard'
import { callOttoPortal, relativeTime } from './ottoPortal'

// Default for locations not yet installed anywhere: new locations go to the
// current server. Installed locations use the URL the partner API reports,
// which follows the marketplace app that installed them. Staging builds set
// VITE_OTTO_CONNECTOR_URL so testers are never sent to the production server.
const DEFAULT_CONNECTOR_URL =
  import.meta.env.VITE_OTTO_CONNECTOR_URL || 'https://www.ottoconnect.ai/mcp'

function StatusRow({ ok, label, detail }: { ok: boolean; label: string; detail: string }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">{detail}</p>
      </div>
      <Badge variant={ok ? 'default' : 'secondary'}>
        {ok ? <Check className="mr-1 h-3 w-3" /> : <Circle className="mr-1 h-3 w-3" />}
        {ok ? 'Connected' : 'Not connected'}
      </Badge>
    </div>
  )
}

// The whole app for a Otto-only operator: who they are, whether Otto is
// wired up, how to connect Claude, and a disconnect escape hatch.
// adminAccount is set when a platform admin views this via impersonation —
// status plus the account-wide reset; no self-disconnect (it would act on
// the admin's own grants).
export function OttoStatusPage({ adminAccount }: { adminAccount?: Account } = {}) {
  const { account: ownAccount } = useAuth()
  const account = adminAccount ?? ownAccount
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const statusQuery = useQuery({
    queryKey: ['otto-status', account?.id],
    queryFn: () =>
      callOttoPortal(adminAccount ? { action: 'status', accountId: adminAccount.id } : { action: 'status' }),
    enabled: !!account?.id,
    refetchInterval: 30_000,
    retry: 1,
  })

  const disconnectMutation = useMutation({
    mutationFn: () => callOttoPortal({ action: 'disconnect' }),
    onSuccess: (result) => {
      const revoked = (result as unknown as { revokedFamilies?: number }).revokedFamilies ?? 0
      toast({
        title: revoked > 0 ? 'Claude disconnected' : 'Nothing was connected',
        description:
          revoked > 0
            ? 'Your Claude connection has been revoked. Reconnect anytime from Claude settings.'
            : 'There was no active Claude connection to disconnect.',
      })
      queryClient.invalidateQueries({ queryKey: ['otto-status', account?.id] })
    },
    onError: () =>
      toast({ title: 'Disconnect failed', description: 'Please try again in a minute.', variant: 'destructive' }),
  })

  const copy = (value: string, label: string) => {
    void navigator.clipboard.writeText(value)
    toast({ title: `${label} copied` })
  }

  const status = statusQuery.data
  const mappingMissing = statusQuery.error?.message === 'no_location_mapping'
  const connectorUrl = status?.connectorUrl ?? DEFAULT_CONNECTOR_URL

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Otto AI</h1>
        <p className="text-muted-foreground">
          Your AI assistant for {account?.name ?? 'your business'} — ask it anything about your leads,
          quotes, and follow-ups, right inside Claude.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Account name</span>
            <span className="text-sm font-medium">{account?.name}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Location ID</span>
            {status?.locationId ? (
              <button
                className="flex items-center gap-1 text-sm font-mono"
                onClick={() => copy(status.locationId, 'Location ID')}
              >
                {status.locationId} <Copy className="h-3 w-3 text-muted-foreground" />
              </button>
            ) : (
              <span className="text-sm text-muted-foreground">{mappingMissing ? 'Not set up yet' : '—'}</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Status</CardTitle>
          {mappingMissing && (
            <CardDescription>
              {account?.account_type === 'miles_only'
                ? "Your account setup isn't finished — please contact Limo Marketer support."
                : 'No GoHighLevel location is linked yet — set it under Settings → Integrations, then come back here.'}
            </CardDescription>
          )}
          {statusQuery.isError && !mappingMissing && (
            <CardDescription>
              Status is temporarily unavailable.{' '}
              <button className="underline" onClick={() => statusQuery.refetch()}>
                Retry
              </button>
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="divide-y">
          {statusQuery.isLoading ? (
            <div className="space-y-3 py-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          ) : status ? (
            <>
              <StatusRow
                ok={status.appInstalled}
                label="Data connection"
                detail={
                  status.appInstalled
                    ? 'Otto can read your CRM data.'
                    : 'App not installed yet — contact Limo Marketer support to finish setup.'
                }
              />
              <StatusRow
                ok={status.anyoneConnected ?? status.claudeConnected}
                label="Claude connection"
                detail={
                  status.claudeConnected
                    ? 'Your Claude is connected to Otto AI.'
                    : status.anyoneConnected
                      ? adminAccount
                        ? 'This account has Claude connected (see team members below).'
                        : 'A teammate is connected. Connect your own Claude using the steps below.'
                      : 'Connect from Claude using the steps below.'
                }
              />
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Last activity</p>
                  <p className="text-sm text-muted-foreground">
                    {status.lastActivityAt
                      ? `Otto last answered ${relativeTime(status.lastActivityAt)}.`
                      : 'No activity yet.'}
                  </p>
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      <OttoLocationUsersCard adminAccount={adminAccount} />

      <OttoLaCard adminAccount={adminAccount} />

      <Card>
        <CardHeader>
          <CardTitle>Connect Claude</CardTitle>
          <CardDescription>Takes about a minute. You&apos;ll need the Claude app or claude.ai.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ol className="list-decimal space-y-2 pl-5">
            <li>In Claude, open Settings → Connectors.</li>
            <li>Choose &quot;Add custom connector&quot;.</li>
            <li>
              Name it <span className="font-medium">Otto AI</span> and paste this URL:
            </li>
          </ol>
          <button
            className="flex w-full items-center justify-between rounded-md border bg-muted px-3 py-2 font-mono text-xs"
            onClick={() => copy(connectorUrl, 'Connector URL')}
          >
            {connectorUrl} <Copy className="h-3 w-3" />
          </button>
          <ol className="list-decimal space-y-2 pl-5" start={4}>
            <li>Click Connect and sign in with this Booked Rides email and password.</li>
            <li>Start a new chat and ask: &quot;Check my Otto AI connection.&quot;</li>
          </ol>
        </CardContent>
      </Card>

      {adminAccount && <OttoResetCard adminAccount={adminAccount} />}

      {!adminAccount && (
        <Card>
          <CardHeader>
            <CardTitle>Disconnect</CardTitle>
            <CardDescription>
              Immediately revokes Claude&apos;s access to your data. You can reconnect anytime.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={!status?.claudeConnected || disconnectMutation.isPending}>
                  {disconnectMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Unplug className="mr-2 h-4 w-4" />
                  )}
                  Disconnect Claude
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disconnect Claude from Otto AI?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Claude will immediately lose access to your CRM data. To use Otto again you&apos;ll
                    reconnect from Claude settings with your Booked Rides login.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep connected</AlertDialogCancel>
                  <AlertDialogAction onClick={() => disconnectMutation.mutate()}>
                    Disconnect
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
